import React, { useEffect, useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { LayoutDashboard, MapPin, ShoppingCart, Users, Truck, Car, Boxes, Tag, Receipt, LifeBuoy, BarChart3, ScrollText, Fuel, LogOut, Bell, Menu, Sparkles, Star } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { ChangePasswordDialog } from '@/components/ChangePassword';
import { ThemeToggle } from '@/components/ThemeToggle';

const nav = [
  { to: '/admin', icon: LayoutDashboard, label: 'Dashboard', end: true },
  { to: '/admin/pump', icon: Fuel, label: 'Petrol Pump' },
  { to: '/admin/ai', icon: Sparkles, label: 'AI Assistant' },
  { to: '/admin/live-map', icon: MapPin, label: 'Live Map' },
  { to: '/admin/orders', icon: ShoppingCart, label: 'Orders' },
  { to: '/admin/customers', icon: Users, label: 'Customers' },
  { to: '/admin/drivers', icon: Truck, label: 'Drivers' },
  { to: '/admin/vehicles', icon: Car, label: 'Vehicles' },
  { to: '/admin/inventory', icon: Boxes, label: 'Inventory' },
  { to: '/admin/pricing', icon: Tag, label: 'Pricing' },
  { to: '/admin/billing', icon: Receipt, label: 'Invoices & Payments' },
  { to: '/admin/support', icon: LifeBuoy, label: 'Support' },
  { to: '/admin/reviews', icon: Star, label: 'Reviews' },
  { to: '/admin/reports', icon: BarChart3, label: 'Reports' },
  { to: '/admin/audit', icon: ScrollText, label: 'Audit Logs' },
];

export const AdminLayout = ({ children }) => {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const [notifs, setNotifs] = useState([]);
  const load = () => api.get('/notifications').then((r) => setNotifs(r.data)).catch(() => {});
  useEffect(() => { load(); const t = setInterval(load, 15000); return () => clearInterval(t); }, []);
  const unread = notifs.filter((n) => !n.read).length;

  return (
    <div className="portal-admin min-h-screen bg-background flex">
      <aside className={`fixed lg:static z-30 inset-y-0 left-0 w-64 bg-card border-r border-border transform transition-transform ${open ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}`}>
        <div className="h-14 flex items-center gap-2 px-4 border-b border-border">
          <img src="/assets/logo.png" alt="Mobile Fuel Kart" className="h-7" />
          <span className="text-[10px] text-muted-foreground uppercase tracking-wide ml-auto">Admin</span>
        </div>
        <div className="py-3 overflow-y-auto no-scrollbar" style={{ maxHeight: 'calc(100vh - 56px)' }}>
          {nav.map((it) => (
            <NavLink key={it.to} to={it.to} end={it.end} onClick={() => setOpen(false)}
              className={({ isActive }) => `flex items-center gap-3 px-4 py-2.5 text-sm ${isActive ? 'bg-muted text-primary font-medium border-r-2 border-primary' : 'text-muted-foreground hover:bg-muted/60'}`}
              data-testid={`admin-nav-${it.label.toLowerCase().replace(/[^a-z]+/g, '-')}`}>
              <it.icon className="h-4 w-4" />{it.label}
            </NavLink>
          ))}
        </div>
      </aside>
      {open && <div className="fixed inset-0 bg-black/30 z-20 lg:hidden" onClick={() => setOpen(false)} />}
      <div className="flex-1 min-w-0">
        <header className="sticky top-0 z-10 bg-card/80 backdrop-blur-xl border-b border-border h-14 flex items-center justify-between px-4">
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="icon" className="lg:hidden" onClick={() => setOpen(true)}><Menu className="h-5 w-5" /></Button>
            <span className="text-sm text-muted-foreground">{user?.admin_role?.replace(/_/g, ' ')?.replace(/\b\w/g, (c) => c.toUpperCase()) || 'Admin'}</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="relative">
              <Button variant="ghost" size="icon" onClick={() => api.post('/notifications/read-all').then(load)}><Bell className="h-5 w-5" />
                {unread > 0 && <span className="absolute top-1 right-1 h-4 w-4 rounded-full bg-destructive text-[10px] text-white flex items-center justify-center">{unread}</span>}
              </Button>
            </div>
            <span className="text-sm font-medium hidden sm:block">{user?.name}</span>
            <ThemeToggle />
            <ChangePasswordDialog />
            <Button variant="ghost" size="icon" onClick={logout} data-testid="logout-button"><LogOut className="h-5 w-5" /></Button>
          </div>
        </header>
        <main className="p-4 sm:p-6 max-w-[1440px] mx-auto">{children}</main>
      </div>
    </div>
  );
};
